/**
 * useNavigation Hook
 *
 * Provides dynamic navigation items built from the route registry.
 * Automatically updates when plugins register/unregister routes.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRouteRegistry } from '@lognai/frontend-core';
import {
  buildNavigationFromRoutes,
  getActiveRouteId,
  NavItemConfig,
  NavSectionMetadata,
  PluginNavMetadata,
} from '@lognai/frontend-core';
import { useAuth } from './useAuth';
import { useIsExternalPartner } from './useIsExternalPartner';

/**
 * Navigation sections — one rail entry each.
 *
 * The rail used to carry one entry per plugin, which put 17 icons in it for a
 * modestly-entitled company and was heading for ~24 with everything switched
 * on; five of those led to a single page. Plugins now map into these sections
 * instead, so the rail is a fixed set of domains and enabling a plugin adds a
 * line to a panel rather than another icon to scan.
 *
 * The order is the shape of the business: how you buy, what you sell, what
 * you hold, what you ship it on, what moves, what moves it, what it costs,
 * what it tells you, who may do it.
 *
 * `command-center` is a single page, so it is a rail link rather than a panel.
 *
 * `caption` names the group a single-plugin section's items sit under in the
 * panel, as the multi-plugin sections' captions do.
 */
export const NAV_SECTIONS: Record<string, NavSectionMetadata> = {
  workspace: { displayName: 'Workspace', order: 1, icon: 'PanelsTopLeft', description: 'Your home, tasks and drafts', caption: 'Workspace' },
  'command-center': { displayName: 'Command Center', order: 2, railLink: true },
  'procure-ai': { displayName: 'Procure AI', order: 3, icon: 'ClipboardList', description: 'Vendors, RFQs and invoice audit' },
  orders: { displayName: 'Orders', order: 4, icon: 'ShoppingCart', description: 'Purchase and sales orders', caption: 'Orders' },
  inventory: { displayName: 'Inventory', order: 5, icon: 'Package', description: 'Warehouses and stock' },
  transport: { displayName: 'Freight', order: 6, icon: 'Truck', description: 'TRFs, freight RFQs and sailings' },
  shipment: { displayName: 'Shipment', order: 7, icon: 'Container', description: 'Shipments, tracking and D&D' },
  fleetmon: { displayName: 'FleetMon', order: 8, icon: 'SteeringWheel', description: 'Vehicles, drivers and trips', caption: 'FleetMon' },
  finance: { displayName: 'Finance', order: 9, icon: 'DollarSign', description: 'Costs, invoices and audit' },
  insights: { displayName: 'Insights', order: 10, icon: 'Sparkles', description: 'Dashboards and analytics', caption: 'AI Insights' },
  admin: { displayName: 'Admin', order: 11, icon: 'Settings', description: 'Users, roles and master data' },
};

/**
 * Plugin metadata for navigation grouping.
 *
 * `section` picks the rail entry the plugin's pages appear under; `order`
 * places its block within that section, and `displayName` captions the block
 * when the section holds more than one plugin. A plugin with no `section`
 * keeps its old behaviour — a rail entry of its own, ordered by `order` —
 * which is the fallback a newly added plugin lands on until it is mapped here.
 */
export const PLUGIN_NAV_METADATA: Record<string, PluginNavMetadata> = {
  // --- Workspace ---
  tasks: { displayName: 'Collaboration', order: 2, section: 'workspace', icon: 'ChatsCircle' },

  // --- Procure AI: who we buy from, how we buy, and checking what we're billed ---
  'vendor-onboarding': { displayName: 'Vendor Onboarding', order: 1, section: 'procure-ai', icon: 'UserPlus' },
  procurement: { displayName: 'Procurement RFQ', order: 2, section: 'procure-ai', icon: 'ShoppingBag' },
  rfq: { displayName: 'RFQ', order: 3, section: 'procure-ai', icon: 'FileText' },
  // Auditing a carrier's invoice against what was agreed is the tail end of
  // buying, so it sits with procurement rather than with the ledger.
  'freight-audit': { displayName: 'Audit Invoice', order: 4, section: 'procure-ai', icon: 'ReceiptText' },

  // --- Orders ---
  orders: { displayName: 'Orders', order: 1, section: 'orders', icon: 'ShoppingCart' },

  // --- Inventory: what we hold, and where ---
  warehouse: { displayName: 'Warehouse', order: 1, section: 'inventory', icon: 'Warehouse' },
  inventory: { displayName: 'Inventory', order: 2, section: 'inventory', icon: 'Stack' },
  products: { displayName: 'Products', order: 3, section: 'inventory', icon: 'Package' },

  // --- Freight: securing the move (section id stays `transport`) ---
  // Transport request forms, with temporary import and export permits where one is needed.
  'temporary-permits': { displayName: 'TRF', order: 1, section: 'transport', icon: 'Certificate' },
  // Buying the freight itself.
  'freight-rfq': { displayName: 'Freight RFQ', order: 2, section: 'transport', icon: 'Boat', showCaption: true },
  // Finding a sailing for the move.
  'ocean-schedules': { displayName: 'Ocean Schedules', order: 3, section: 'transport' },

  // --- Shipment: what is moving ---
  shipments: { displayName: 'Shipments', order: 1, section: 'shipment', icon: 'Container' },
  tracking: { displayName: 'Tracking', order: 3, section: 'shipment', icon: 'Crosshair' },
  // D&D draws its container data from Shipments, so it reads as part of the
  // same story rather than a section of its own.
  dd: { displayName: 'Demurrage & Detention', order: 4, section: 'shipment', icon: 'Clock' },

  // --- FleetMon: the vehicles and drivers that do the moving ---
  // A rail entry of its own: running a fleet is a day's work in itself, not
  // one block among the shipment pages.
  fleetmon: { displayName: 'FleetMon', order: 1, section: 'fleetmon' },

  // --- Finance: what it costs, and what we owe ---
  finance: { displayName: 'Finance', order: 1, section: 'finance', icon: 'Wallet' },
  invoices: { displayName: 'Invoices', order: 2, section: 'finance', icon: 'Receipt' },
  // Landed cost is shipment-driven but it is a costing answer, so it belongs
  // with the money rather than with the movement.
  'landed-cost': { displayName: 'Landed Cost', order: 3, section: 'finance', icon: 'Calculator' },
  'exchange-rates': { displayName: 'Exchange Rates', order: 4, section: 'finance', icon: 'ArrowsLeftRight' },
  'hs-classifier': { displayName: 'HS Classifier', order: 5, section: 'finance', icon: 'Tag' },

  // --- Insights ---
  analytics: { displayName: 'Analytics', order: 2, section: 'insights', icon: 'ChartBar', showCaption: true },

  // --- Admin ---
  admin: { displayName: 'Admin', order: 1, section: 'admin', icon: 'Gear' },
  'master-data': { displayName: 'Master Data', order: 2, section: 'admin', icon: 'Database' },

  // --- Workflows: no section of its own. Each published form names the
  // section it appears in, and the platform Workflows page sits under Admin;
  // wherever they land they are captioned "Workflows".
  workflows: { displayName: 'Workflows', order: 6, icon: 'FileText', showCaption: true },
};

interface UseNavigationOptions {
  /** Whether to group nav items by plugin with separators (default: true) */
  groupByPlugin?: boolean;
}

interface UseNavigationOptionsWithExtras extends UseNavigationOptions {
  /**
   * App-level nav items to slot into a section, keyed by section id. The
   * caller owns these (the workspace home, Drafts, an entitled external tool);
   * passing them here places them by section and `order` instead of splicing
   * them into the built list by index.
   */
  sectionExtras?: Record<string, NavItemConfig[]>;
}

interface UseNavigationResult {
  /** Main navigation items */
  navItems: NavItemConfig[];

  /** Footer navigation items */
  footerItems: NavItemConfig[];

  /** ID of the currently active nav item */
  activeItemId?: string;

  /** Whether navigation is loading */
  isLoading: boolean;
}

/**
 * Hook to get dynamic navigation items from route registry
 *
 * @param options - Navigation options
 */
export function useNavigation(options: UseNavigationOptionsWithExtras = {}): UseNavigationResult {
  const { groupByPlugin = true, sectionExtras } = options;

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isExternalPartner = useIsExternalPartner();
  const [navItems, setNavItems] = useState<NavItemConfig[]>([]);
  const [footerItems, setFooterItems] = useState<NavItemConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize plugin metadata to avoid unnecessary re-renders
  const pluginMetadata = useMemo(() => PLUGIN_NAV_METADATA, []);

  // Navigation handler
  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  // Build navigation from routes
  const buildNavigation = useCallback(() => {
    try {
      // Get user permissions
      const userPermissions = user?.permissions || [];

      // Build main navigation items from routes
      const items = buildNavigationFromRoutes({
        userPermissions,
        userAudience: isExternalPartner ? 'partner' : 'internal',
        currentPath: location.pathname,
        onNavigate: handleNavigate,
        groupByPlugin,
        pluginMetadata,
        navSections: NAV_SECTIONS,
        ...(sectionExtras ? { sectionExtras } : {}),
      });

      setNavItems(items);

      // TODO: Build footer items from routes
      // For now, keep footer items empty (will be set by MainLayout)
      setFooterItems([]);

      setIsLoading(false);
    } catch (error) {
      console.error('[useNavigation] Error building navigation:', error);
      setNavItems([]);
      setFooterItems([]);
      setIsLoading(false);
    }
  }, [
    location.pathname,
    user?.permissions,
    isExternalPartner,
    handleNavigate,
    groupByPlugin,
    pluginMetadata,
    sectionExtras,
  ]);

  // Subscribe to route registry changes
  useEffect(() => {
    const registry = getRouteRegistry();

    // Build initial navigation
    buildNavigation();

    // Subscribe to route changes
    const unsubscribe = registry.subscribe(() => {
      console.log('[useNavigation] Routes changed, rebuilding navigation');
      buildNavigation();
    });

    return unsubscribe;
  }, [buildNavigation]);

  // Determine active item from current path
  const activeItemId = getActiveRouteId(location.pathname);

  return {
    navItems,
    footerItems,
    activeItemId,
    isLoading,
  };
}
